import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "panel"]
  static values = {
    autoOpen: { type: Boolean, default: false },
    // "slide-over" animates the panel from the right edge at every viewport
    // width; the default is a centered dialog (slide-up on mobile, scale-in
    // on desktop).
    variant: { type: String, default: "dialog" }
  }

  connect() {
    this._boundCloseOnEscape = this._closeOnEscape.bind(this)
    this._boundTrapFocus = this._trapFocus.bind(this)
    document.addEventListener("keydown", this._boundCloseOnEscape)

    if (this.autoOpenValue) this.open()
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundCloseOnEscape)
    document.removeEventListener("keydown", this._boundTrapFocus)
    document.body.classList.remove("overflow-hidden")
  }

  open() {
    this.containerTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    const backdrop = this.containerTarget.querySelector("[data-modal-backdrop]")
    if (backdrop) backdrop.classList.add("animate-fade-in")

    if (this.hasPanelTarget) {
      if (this.variantValue === "slide-over") {
        this.panelTarget.classList.add("animate-slide-in-right")
      } else if (window.innerWidth < 768) {
        this.panelTarget.classList.add("animate-slide-up")
      } else {
        this.panelTarget.classList.add("animate-scale-in")
      }
    }

    document.addEventListener("keydown", this._boundTrapFocus)

    requestAnimationFrame(() => {
      const focusable = this._getFocusableElements()
      if (focusable.length > 0) focusable[0].focus()
    })
  }

  close() {
    const backdrop = this.containerTarget.querySelector("[data-modal-backdrop]")
    if (backdrop) {
      backdrop.classList.remove("animate-fade-in")
      backdrop.classList.add("animate-fade-out")
    }

    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("animate-scale-in", "animate-slide-up", "animate-slide-in-right")
      if (this.variantValue === "slide-over") {
        this.panelTarget.classList.add("animate-slide-out-right")
      } else if (window.innerWidth < 768) {
        this.panelTarget.classList.add("animate-slide-down")
      } else {
        this.panelTarget.classList.add("animate-scale-out")
      }
    }

    setTimeout(() => {
      this.containerTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
      document.removeEventListener("keydown", this._boundTrapFocus)

      if (backdrop) backdrop.classList.remove("animate-fade-out")
      if (this.hasPanelTarget) {
        this.panelTarget.classList.remove("animate-scale-out", "animate-slide-down", "animate-slide-out-right")
      }
    }, 200)
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  _closeOnEscape(event) {
    if (event.key === "Escape" && !this.containerTarget.classList.contains("hidden")) {
      this.close()
    }
  }

  _trapFocus(event) {
    if (event.key !== "Tab") return

    const focusable = this._getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  _getFocusableElements() {
    return [...this.panelTarget.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )]
  }
}
